// Flow scenarios = the user-composable "cases" shown as tabs in the junction
// turning matrices. Two built-in leaves — Existing (surveyed baseline, editable)
// and Development (the assignment for the selected development scenario) — plus
// any number of user-defined scenarios built by:
//   • growth  — a base scenario multiplied by a per-period growth factor
//                (Future year = Existing × 1.10)
//   • sum     — two or more scenarios added cell by cell
//                (Future year + development = Future year + Development)
// Growth lives here, on the flow-scenario axis, not on project-setup scenarios.

export const BUILTIN_IDS = ['existing', 'development'];

// Default set for a new project: just the two built-in leaves. The user builds
// any combined/grown scenarios (e.g. Existing + development, Future year) themselves.
export const DEFAULT_FLOW_SCENARIOS = [
  { id: 'existing', name: 'Existing (survey)', kind: 'existing' },
  { id: 'development', name: 'Development', kind: 'development' },
];

export function isBuiltIn(id) {
  return BUILTIN_IDS.includes(id);
}

// Evaluate one flow scenario's value for a single movement (from → to arm).
//   list  — the full flowScenarios array
//   id    — which scenario to evaluate
//   ctx   — { exAt(from,to), devAt(from,to), growthMult(fs) }
//           exAt/devAt are the two leaves for THIS junction; growthMult returns
//           the multiplier for a growth scenario in the active period.
//   seen  — internal cycle guard (a scenario referencing itself → 0).
export function evalFlowCell(list, id, from, to, ctx, seen = new Set()) {
  if (seen.has(id)) return 0;               // reference cycle — bail safely
  const fs = list.find(s => s.id === id);
  if (!fs) return 0;
  const next = new Set(seen); next.add(id);
  switch (fs.kind) {
    case 'existing':
      return Math.round(ctx.exAt(from, to) || 0);
    case 'development':
      return Math.round(ctx.devAt(from, to) || 0);
    case 'growth':
      return Math.round(evalFlowCell(list, fs.baseId, from, to, ctx, next) * (ctx.growthMult(fs) || 1));
    case 'sum':
      return (fs.componentIds || []).reduce(
        (acc, cid) => acc + evalFlowCell(list, cid, from, to, ctx, next), 0);
    default:
      return 0;
  }
}

// Only the Existing leaf is hand-editable (surveyed counts); everything else is
// derived and read-only.
export function isEditable(fs) {
  return !!fs && fs.kind === 'existing';
}

// Human-readable one-line description of how a derived scenario is built, for the
// status line under the tabs. `nameOf` maps an id → display name.
export function describe(fs, nameOf) {
  if (!fs) return '';
  if (fs.kind === 'existing') return 'Surveyed turning counts (veh/hr). Type in cells or paste from Excel.';
  if (fs.kind === 'development') return 'Development traffic from the schematic assignment. Read-only.';
  if (fs.kind === 'growth') return `${nameOf(fs.baseId)} grown by the period growth factor. Read-only.`;
  if (fs.kind === 'sum') return `${(fs.componentIds || []).map(nameOf).join(' + ')}, summed per movement. Read-only.`;
  return '';
}
